import { useContainerBlockContext } from '@lighthouse/block'
import type { DataSourceAbstract, FilterCommonCondition, QuickFilterConfig } from '@lighthouse/core'
import { getQuickFilterRule, getWithScopeId, useApplicationContext, useAtomAction } from '@lighthouse/shared'
import { useAtomValue } from 'jotai'
import { useCallback, useMemo } from 'react'
import { useUpdateEffect } from 'react-use'

import { quickFilterCacheAtomFamily } from '@/atoms/storage/state'

interface useQuickFilterPayload {
    appId: string
    envId: string
    blockId: string
    dataSource: DataSourceAbstract | undefined
    quickFilter: QuickFilterConfig
}

export const useQuickFilter = (payload: useQuickFilterPayload) => {
    const { appId, envId, blockId, dataSource, quickFilter } = payload
    const { scope } = useContainerBlockContext()
    const withScopeId = getWithScopeId(blockId, scope)
    const { run: setQuickFilterCache } = useAtomAction(quickFilterCacheAtomFamily({ appId, envId, id: withScopeId }))
    const { personOptions } = useApplicationContext()
    const quickFiltersCache = useAtomValue(quickFilterCacheAtomFamily({ appId, envId, id: withScopeId }))
    const quickFiltersRule = useMemo(() => {
        if (!dataSource) {
            return []
        }
        return getQuickFilterRule({ quickFiltersCache, quickFilter, dataSource, personOptions })
    }, [dataSource, personOptions, quickFilter, quickFiltersCache])

    const handleQuickFilter = useCallback(
        (data: FilterCommonCondition[]) => {
            setQuickFilterCache(data)
        },
        [setQuickFilterCache]
    )

    useUpdateEffect(() => {
        if (quickFilter.mode === 'disable') {
            setQuickFilterCache(undefined)
        }
    }, [quickFilter.mode])

    return { quickFiltersRule, handleQuickFilter }
}
